/**
 * sessionSnapshot — the ONE atomic browser session record (the app model's
 * persistence owner, sweep contract §4).
 *
 * ONE sessionStorage key holds the whole session state: the bearer (persisted
 * AS-IS with its real expiry — the window is NOT shortened here; scheduling
 * safety margins live at the consumer), the active context (migrated from the
 * legacy `causit_active_context` localStorage record), and the channel feed
 * store dump. Every change REPLACES the whole record atomically — no partial
 * writes, no second storage key, no TTL freshness (content identity and ws
 * frames decide freshness; this record is a CACHE of served state that the
 * reconcile drains complete).
 *
 * Size discipline (THE LIMIT LAW): the feed dump is bounded newest-first as a
 * cache of served pieces — never a silent cap presented as complete data; a
 * quota overflow drops the feed slot LOUDLY (the drain rebuilds it) before it
 * ever drops auth/context.
 */

const SESSION_SNAPSHOT_KEY = "causit_session";

export type SessionAuth = {
  /** the normalized "Bearer <jwt>" exactly as the door returned it */
  token: string;
  /** the door's real expiry epoch-ms — persisted UNSHORTENED */
  expiresAt: number;
  user: unknown;
  activeUser: unknown;
  activeOrg: unknown;
  userQuotas?: unknown;
  location?: unknown;
};

export type SessionSnapshotRecord = {
  auth: SessionAuth | null;
  activeContext: unknown;
  feed: unknown;
};

const EMPTY: SessionSnapshotRecord = { auth: null, activeContext: null, feed: null };

const storage = (): Storage | null => {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
};

export const readSessionSnapshot = (): SessionSnapshotRecord => {
  const store = storage();
  if (!store) return { ...EMPTY };
  const raw = store.getItem(SESSION_SNAPSHOT_KEY);
  if (!raw) return { ...EMPTY };
  try {
    const parsed = JSON.parse(raw) as Partial<SessionSnapshotRecord>;
    return {
      auth: parsed.auth && typeof parsed.auth === "object" ? parsed.auth as SessionAuth : null,
      activeContext: parsed.activeContext ?? null,
      feed: parsed.feed ?? null,
    };
  } catch (error) {
    // a corrupt snapshot is LOUD, then cleared — never a silent half-state.
    console.error("[session-snapshot] corrupt session record — clearing it", error);
    store.removeItem(SESSION_SNAPSHOT_KEY);
    return { ...EMPTY };
  }
};

const writeWhole = (next: SessionSnapshotRecord): void => {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(next));
  } catch (error) {
    // quota: the feed dump is the cache slot — drop it LOUDLY and keep the
    // auth/context state (the drain rebuilds the feed; auth must never lose).
    console.error("[session-snapshot] atomic write failed — retrying without the feed cache slot", error);
    try {
      store.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify({ ...next, feed: null }));
    } catch (retryError) {
      console.error("[session-snapshot] session persistence unavailable", retryError);
    }
  }
};

/** Replace ONE slot inside the ONE record — read-modify-replace, atomic. */
export const replaceSessionSlot = <K extends keyof SessionSnapshotRecord>(
  slot: K,
  value: SessionSnapshotRecord[K],
): SessionSnapshotRecord => {
  const next = { ...readSessionSnapshot(), [slot]: value };
  writeWhole(next);
  return next;
};

export const clearSessionSnapshot = (): void => {
  const store = storage();
  if (!store) return;
  store.removeItem(SESSION_SNAPSHOT_KEY);
};
